import Moment from 'moment-timezone';
import { getMomentLocaleTimeFormat } from '../Calendar-util';
import {
  ATTENDANCE_STATUS,
  CANCEL_STATUS,
  DEFER_SOURCE,
  DELIVERY_MODE,
  DIAGNOSTICS_LANE_KEY,
  DISPLAY_STATUS,
  DISPLAY_STATUS_META,
  ENROL_CATEGORY,
  FILTER_CHIP,
  NEEDS_ATTENTION_STATUSES,
  QUICK_FILTER,
  SESSION_CLASS,
  SETTLEMENT_FLAG,
  STUDENT_AVATAR_COLORS,
  TUTOR_LANE_COLORS,
} from './constants';

/** Coerce the endpoint's stringly-typed numbers, treating null/'' as "absent". */
function toInt(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** True when the calendar row is marked paused (`is_paused` from the API). */
function isPausedRow(row) {
  const value = row?.is_paused;
  if (value === true || value === 1) {
    return true;
  }
  return toInt(value) === 1;
}

/** Cancelled / forfeit / writeoff — no One-off / Deferred placement counts. */
function isVoidStudentStatus(status) {
  return (
    status === DISPLAY_STATUS.CANCELLED ||
    status === DISPLAY_STATUS.FORFEIT ||
    status === DISPLAY_STATUS.WRITEOFF
  );
}

function isOpenWorkflowStatus(status) {
  return (
    status === DISPLAY_STATUS.DEFER_REQUESTED ||
    status === DISPLAY_STATUS.SLOT_ASSIGN ||
    status === DISPLAY_STATUS.ONE_OFF_REQUESTED
  );
}

/** Active one-off placement (not void, not an open request status). */
function isActiveOneOffPlacement(student) {
  return Boolean(student.isOneOff) && !isOpenWorkflowStatus(student.status);
}

/** Active deferred placement (strict defer-source reassignment). */
function isActiveDeferredPlacement(student) {
  return Boolean(student.isDeferred);
}

/** Title-case a name fragment; the endpoint returns inconsistent casing. */
export function capitalize(value) {
  if (!value) {
    return '';
  }
  const text = String(value);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function formatStudentName(row) {
  return `${capitalize(row.st_first_name)} ${row.st_surname || ''}`.trim();
}

/** Two-letter monogram for avatars, e.g. "Emily Sun" becomes "ES". */
export function initialsOf(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/**
 * Abbreviate a year level for the compact chips: "Year 7" becomes "Y7".
 * Values that do not contain a number are passed through untouched.
 */
export function abbreviateYearLevel(yearLevel) {
  if (!yearLevel) {
    return '';
  }
  const match = String(yearLevel).match(/(\d+)/);
  return match ? `Y${match[1]}` : String(yearLevel);
}

/**
 * The attendance record can override the enrolment's delivery category, which is how
 * a one-off switch to Online is stored. Mirrors the legacy `enrolCategory` helper.
 */
export function resolveEnrolCategory(row) {
  return row.attendance_enrol_category || row.enrol_category || '';
}

export function resolveDeliveryMode(row) {
  if (toInt(row.is_flexi) === 1) {
    return DELIVERY_MODE.FLEXI;
  }
  return resolveEnrolCategory(row) === ENROL_CATEGORY.ONLINE
    ? DELIVERY_MODE.ONLINE
    : DELIVERY_MODE.IN_CENTRE;
}

/**
 * Status waterfall for Initial Diagnostics / assessment-slot rows (`isschedule === 1`).
 * Matches production Calendar.js badges: Closed → Completed → Converted → Pending.
 */
export function deriveDiagnosticsStudentStatus(row) {
  const isDiagnostics = toInt(row.is_diagnostics) === 1;
  const isClosed = toInt(row.is_diagnostics_close) === 1;
  const isStudent = toInt(row.is_student) === 1;

  if (isDiagnostics && isClosed && !isStudent) {
    return DISPLAY_STATUS.DIAGNOSTICS_CLOSED;
  }
  if (isDiagnostics && !isStudent) {
    return DISPLAY_STATUS.DIAGNOSTICS_COMPLETED;
  }
  if (isDiagnostics && isStudent) {
    return DISPLAY_STATUS.STUDENT_CONVERTED;
  }
  return DISPLAY_STATUS.PENDING;
}

/**
 * Collapse the overlapping status columns on a student row into one display status.
 */
export function deriveStudentStatus(row, { isDiagnosticsSession = false } = {}) {
  if (isDiagnosticsSession) {
    return deriveDiagnosticsStudentStatus(row);
  }

  const attendance = toInt(row.attendance_status);
  const cancelStatus = toInt(row.cancel_status);
  const deferSource = toInt(row.cancel_defer_sources);
  const isCancelledAttendance = attendance === ATTENDANCE_STATUS.CANCELLED;

  if (toInt(row.fcount) === SETTLEMENT_FLAG.FORFEIT && isCancelledAttendance) {
    return DISPLAY_STATUS.FORFEIT;
  }
  if (toInt(row.wcount) === SETTLEMENT_FLAG.WRITEOFF && isCancelledAttendance) {
    return DISPLAY_STATUS.WRITEOFF;
  }
  // Pause wins over attendance / defer chips so the status pill matches production.
  if (isPausedRow(row)) {
    return DISPLAY_STATUS.PAUSED;
  }
  // One-off assign to another session does not cancel this row — only show Cancelled
  // when the API marks both cancel_status and attendance as cancelled.
  if (cancelStatus === CANCEL_STATUS.CANCELLED && isCancelledAttendance) {
    return DISPLAY_STATUS.CANCELLED;
  }
  if (cancelStatus === CANCEL_STATUS.REQUESTED && deferSource === DEFER_SOURCE.DEFER) {
    return DISPLAY_STATUS.DEFER_REQUESTED;
  }
  if (cancelStatus === CANCEL_STATUS.APPROVED && deferSource === DEFER_SOURCE.DEFER) {
    return DISPLAY_STATUS.SLOT_ASSIGN;
  }
  if (cancelStatus === CANCEL_STATUS.APPROVED) {
    return DISPLAY_STATUS.DEFER_REQUESTED;
  }
  if (cancelStatus === CANCEL_STATUS.ONE_OFF_REQUESTED && deferSource === DEFER_SOURCE.ONE_OFF) {
    return DISPLAY_STATUS.ONE_OFF_REQUESTED;
  }

  switch (attendance) {
    case ATTENDANCE_STATUS.PENDING:
      return DISPLAY_STATUS.PENDING;
    case ATTENDANCE_STATUS.ONGOING:
      return DISPLAY_STATUS.ONGOING;
    case ATTENDANCE_STATUS.ATTENDED:
      return DISPLAY_STATUS.ATTENDED;
    case ATTENDANCE_STATUS.MISSED:
      return DISPLAY_STATUS.MISSED;
    case ATTENDANCE_STATUS.OFFLINE:
      return DISPLAY_STATUS.OFFLINE;
    case ATTENDANCE_STATUS.DEFER_REQUESTED:
      return DISPLAY_STATUS.DEFER_REQUESTED;
    case ATTENDANCE_STATUS.CANCELLED:
      return DISPLAY_STATUS.CANCELLED;
    default:
      return DISPLAY_STATUS.PENDING;
  }
}

/**
 * Statuses 5 and 6 belong to the defer and forfeit workflows; letting an admin
 * overwrite them by hand would desync the cancel-session tables.
 */
export function isAttendanceEditable(row, { isDiagnosticsSession = false } = {}) {
  if (isDiagnosticsSession) {
    return false;
  }
  const attendance = toInt(row.attendance_status);
  return (
    !isPausedRow(row) &&
    attendance !== ATTENDANCE_STATUS.DEFER_REQUESTED &&
    attendance !== ATTENDANCE_STATUS.CANCELLED
  );
}

/**
 * `HH:mm:ss`, `HH:mm`, or a 12-hour value (`1:00 PM`) to a fractional hour.
 * "1:00 PM" is 13, not 1. A bare hour of 1 that is still before the start is
 * corrected later by `endHourAfterStart`.
 */
export function timeToDecimalHour(time) {
  if (!time) {
    return null;
  }
  const raw = String(time).trim();
  const meridian = raw.match(/\b(am|pm)\b/i);
  const match = raw.match(/(\d{1,2})\s*:\s*(\d{2})/);
  if (!match) {
    return null;
  }
  let hour = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10) || 0;
  if (Number.isNaN(hour)) {
    return null;
  }
  if (meridian) {
    const isPm = meridian[1].toLowerCase() === 'pm';
    if (isPm && hour < 12) {
      hour += 12;
    }
    if (!isPm && hour === 12) {
      hour = 0;
    }
  }
  return hour + minutes / 60;
}

/**
 * A 3:00 start with an end of 1:00 is 1:00 PM (13:00), not a one-hour block
 * ending at 4:00. Leave values that are already later than the start unchanged.
 */
export function endHourAfterStart(startHour, endHour) {
  if (startHour === null || startHour === undefined || endHour === null || endHour === undefined) {
    return endHour;
  }
  if (endHour > startHour) {
    return endHour;
  }
  if (endHour + 12 > startHour && endHour + 12 <= 24) {
    return endHour + 12;
  }
  return endHour;
}

/**
 * Render an `HH:mm:ss` time in the centre's locale, so en-AU shows "4:00 PM" while
 * en-SG shows "16:00". Returns an empty string when the time is missing.
 */
export function formatTime(time, locale) {
  return time ? getMomentLocaleTimeFormat(time, 'HH:mm:ss', locale) : '';
}

/** Human duration between two `HH:mm:ss` strings, e.g. "1h" or "1.5h". */
export function formatDuration(startTime, endTime) {
  const start = timeToDecimalHour(startTime);
  const end = timeToDecimalHour(endTime);
  if (start === null || end === null) {
    return '';
  }
  const hours = end >= start ? end - start : end + 24 - start;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

/**
 * Normalise one `description[]` entry.
 */
function buildStudent(row, index, session, { isDiagnosticsSession = false } = {}) {
  const status = deriveStudentStatus(row, { isDiagnosticsSession });
  const deferSource = toInt(row.cancel_defer_sources);
  const meta = DISPLAY_STATUS_META[status] || DISPLAY_STATUS_META[DISPLAY_STATUS.PENDING];
  const isConverted = status === DISPLAY_STATUS.STUDENT_CONVERTED;

  return {
    key: row.id ?? `${session.pk_ses_key}-${row.fk_stu_key}-${index}`,
    studentKey: row.fk_stu_key || row.fk_student_key,
    name: formatStudentName(row),
    initials: initialsOf(formatStudentName(row)),
    avatarColor: STUDENT_AVATAR_COLORS[index % STUDENT_AVATAR_COLORS.length],
    yearLevel: abbreviateYearLevel(row.st_year_level),
    subjectName: row.subject_name || '',
    subjectAbbreviation: row.sub_abbreviation || '',
    status,
    statusLabel: meta.label,
    statusTone: meta.tone,
    isStruckThrough: meta.struckThrough,
    deliveryMode: resolveDeliveryMode(row),
    enrolCategory: resolveEnrolCategory(row),
    attendanceStatus: toInt(row.attendance_status),
    isFlexi: toInt(row.is_flexi) === 1,
    isPaused: isPausedRow(row),
    isReassigned: toInt(row.reassigned) === 1,
    isOneOff: (() => {
      if (isDiagnosticsSession) {
        return false;
      }
      if (isVoidStudentStatus(status) || isOpenWorkflowStatus(status)) {
        return false;
      }
      if (toInt(row.reassigned) !== 1) {
        return false;
      }
      // Explicit defer move stays Deferred; everything else reassigned is One-off.
      return deferSource !== DEFER_SOURCE.DEFER;
    })(),
    isDeferred: (() => {
      if (isDiagnosticsSession) {
        return false;
      }
      if (isVoidStudentStatus(status) || isOpenWorkflowStatus(status)) {
        return false;
      }
      return toInt(row.reassigned) === 1 && deferSource === DEFER_SOURCE.DEFER;
    })(),
    isAttendanceEditable: isAttendanceEditable(row, { isDiagnosticsSession }),
    // Converted diagnostics students may open the student profile; others go to /Diagnostics.
    canNavigateToProfile: isDiagnosticsSession
      ? isConverted && toInt(row.stu_visibility) === 1
      : toInt(row.stu_visibility) === 1,
    isDiagnosticsStudent: isDiagnosticsSession,
    hasOpenDeferWorkflow: isDiagnosticsSession ? false : toInt(row.cancel_status) !== null,
    raw: row,
  };
}

/**
 * Pick the status that represents a whole session in one pill.
 */
export function deriveSessionStatus(students) {
  const precedence = [
    DISPLAY_STATUS.ONGOING,
    DISPLAY_STATUS.DEFER_REQUESTED,
    DISPLAY_STATUS.SLOT_ASSIGN,
    DISPLAY_STATUS.ONE_OFF_REQUESTED,
    DISPLAY_STATUS.MISSED,
    DISPLAY_STATUS.PAUSED,
    DISPLAY_STATUS.DIAGNOSTICS_CLOSED,
    DISPLAY_STATUS.DIAGNOSTICS_COMPLETED,
    DISPLAY_STATUS.STUDENT_CONVERTED,
    DISPLAY_STATUS.PENDING,
    DISPLAY_STATUS.ATTENDED,
    DISPLAY_STATUS.OFFLINE,
    DISPLAY_STATUS.FORFEIT,
    DISPLAY_STATUS.WRITEOFF,
    DISPLAY_STATUS.CANCELLED,
  ];

  const found = precedence.find((status) => students.some((student) => student.status === status));
  return found || DISPLAY_STATUS.PENDING;
}

/**
 * Normalise one session event.
 */
export function buildSession(event, laneColorIndex, locale) {
  const isDiagnostics = toInt(event.isschedule) === 1;
  const rawStudents = Array.isArray(event.description) ? event.description : [];
  const students = rawStudents.map((row, index) =>
    buildStudent(row, index, event, { isDiagnosticsSession: isDiagnostics })
  );
  const status = deriveSessionStatus(students);
  const sessionClass = toInt(event.sest_class);
  const startHour = timeToDecimalHour(event.sest_start_time);
  const parsedEndHour = timeToDecimalHour(event.ses_end_time);
  const endHour = endHourAfterStart(startHour, parsedEndHour);
  const endForLabel =
    endHour !== null && endHour !== parsedEndHour
      ? `${String(Math.floor(endHour)).padStart(2, '0')}:${String(
          Math.round((endHour - Math.floor(endHour)) * 60)
        ).padStart(2, '0')}:00`
      : event.ses_end_time;
  const startTimeLabel = formatTime(event.sest_start_time, locale);
  const endTimeLabel = formatTime(endForLabel, locale);
  const statusMeta = DISPLAY_STATUS_META[status] || DISPLAY_STATUS_META[DISPLAY_STATUS.PENDING];

  return {
    startTimeLabel,
    endTimeLabel,
    timeRangeLabel: [startTimeLabel, endTimeLabel].filter(Boolean).join(' - '),
    key:
      event.pk_ses_key ||
      `${isDiagnostics ? 'diag' : 'ses'}-${event.fk_sest_key}-${event.ses_date}-${event.sest_start_time}`,
    sessionKey: event.pk_ses_key,
    sessionTemplateKey: event.fk_sest_key,
    // Assessment slots share one day-view lane; card title still uses API `title`.
    tutorKey: isDiagnostics ? DIAGNOSTICS_LANE_KEY : event.fk_tut_key,
    tutorName: event.title || (isDiagnostics ? 'Initial Diagnostics' : ''),
    date: event.ses_date,
    startTime: event.sest_start_time,
    endTime: event.ses_end_time,
    startHour,
    endHour,
    durationLabel: formatDuration(event.sest_start_time, endForLabel),
    templateName: event.sest_name || '',
    description: event.sest_description || '',
    sessionType: isDiagnostics ? 'Initial Diagnostics' : event.sest_type || '',
    sessionClass,
    isOneOffClass: !isDiagnostics && sessionClass === SESSION_CLASS.ONE_OFF,
    isMakeUpClass: !isDiagnostics && sessionClass === SESSION_CLASS.MAKE_UP,
    isDiagnostics,
    laneColorIndex,
    students,
    status,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    isLive: !isDiagnostics && status === DISPLAY_STATUS.ONGOING,
    ongoingCount: students.filter((student) => student.status === DISPLAY_STATUS.ONGOING).length,
    lessonCount: students.length,
    hasFlexiStudent: students.some((student) => student.isFlexi),
    oneOffCount: students.filter((student) => isActiveOneOffPlacement(student)).length,
    deferredCount: students.filter((student) => isActiveDeferredPlacement(student)).length,
    makeUpCount: !isDiagnostics && sessionClass === SESSION_CLASS.MAKE_UP ? students.length : 0,
    raw: event,
  };
}

/**
 * Normalise the whole payload, assigning each tutor a stable lane colour.
 */
export function buildSessions(events, locale) {
  if (!Array.isArray(events)) {
    return [];
  }

  const laneColorByTutor = new Map();

  return events
    .map((event) => {
      const isDiagnostics = toInt(event.isschedule) === 1;
      const tutorKey = isDiagnostics ? DIAGNOSTICS_LANE_KEY : event.fk_tut_key;
      if (!laneColorByTutor.has(tutorKey)) {
        laneColorByTutor.set(tutorKey, laneColorByTutor.size % TUTOR_LANE_COLORS.length);
      }
      return buildSession(event, laneColorByTutor.get(tutorKey), locale);
    })
    .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
}

/**
 * Rebuild the aggregates that depend on the student list.
 */
function withStudents(session, students) {
  const status = deriveSessionStatus(students);
  const statusMeta = DISPLAY_STATUS_META[status] || DISPLAY_STATUS_META[DISPLAY_STATUS.PENDING];

  return {
    ...session,
    students,
    status,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    isLive: !session.isDiagnostics && status === DISPLAY_STATUS.ONGOING,
    ongoingCount: students.filter((student) => student.status === DISPLAY_STATUS.ONGOING).length,
    lessonCount: students.length,
    hasFlexiStudent: students.some((student) => student.isFlexi),
    oneOffCount: students.filter((student) => isActiveOneOffPlacement(student)).length,
    deferredCount: students.filter((student) => isActiveDeferredPlacement(student)).length,
    makeUpCount: session.isMakeUpClass ? students.length : 0,
  };
}

/** Does one student row satisfy the panel's quick filter? */
function matchesQuickFilter(student, quickFilter) {
  switch (quickFilter) {
    case QUICK_FILTER.NEEDS_ATTENTION:
      return NEEDS_ATTENTION_STATUSES.includes(student.status);
    case QUICK_FILTER.ONLINE:
      return student.deliveryMode === DELIVERY_MODE.ONLINE;
    case QUICK_FILTER.IN_CENTRE:
      return student.deliveryMode === DELIVERY_MODE.IN_CENTRE;
    case QUICK_FILTER.FLEXI:
      return student.deliveryMode === DELIVERY_MODE.FLEXI;
    case QUICK_FILTER.ALL:
    default:
      return true;
  }
}

/** Non-status chips handled specially in `matchesStatusFilter` / `filterSessions`. */
const SPECIAL_FILTER_CHIPS = new Set([
  FILTER_CHIP.FLEXI,
  FILTER_CHIP.IN_CENTRE,
  FILTER_CHIP.INITIAL_DIAGNOSTICS,
]);

/**
 * Whether a student matches the Status filter chips (OR across selected chips).
 * `FILTER_CHIP.FLEXI` / `IN_CENTRE` match delivery mode; other chips match `student.status`.
 * `INITIAL_DIAGNOSTICS` is session-level and handled in `filterSessions`.
 */
function matchesStatusFilter(student, statuses) {
  if (!statuses || statuses.length === 0) {
    return true;
  }

  const wantsFlexi = statuses.includes(FILTER_CHIP.FLEXI);
  const wantsInCentre = statuses.includes(FILTER_CHIP.IN_CENTRE);
  const statusKeys = statuses.filter((entry) => !SPECIAL_FILTER_CHIPS.has(entry));
  const matchesStatus = statusKeys.includes(student.status);
  const matchesFlexi =
    wantsFlexi &&
    (student.isFlexi || student.deliveryMode === DELIVERY_MODE.FLEXI);
  const matchesInCentre =
    wantsInCentre && student.deliveryMode === DELIVERY_MODE.IN_CENTRE;

  const chipMatches = [];
  if (wantsFlexi) {
    chipMatches.push(matchesFlexi);
  }
  if (wantsInCentre) {
    chipMatches.push(matchesInCentre);
  }
  const anyChip = chipMatches.length === 0 ? false : chipMatches.some(Boolean);

  if (statusKeys.length === 0) {
    return anyChip;
  }
  if (!wantsFlexi && !wantsInCentre) {
    return matchesStatus;
  }
  return matchesStatus || anyChip;
}

/**
 * Apply the panel's filters.
 *
 * Empty sessions (no students) stay visible so Create Session results appear on the
 * grid, unless a student-level status/quick filter is active. Empty Initial
 * Diagnostics slots stay when that chip is selected.
 */
export function filterSessions(sessions, { tutorKeys, statuses, quickFilter }) {
  const hasTutorFilter = tutorKeys.length > 0;
  const hasStatusFilter = statuses.length > 0;
  const wantsDiagnostics = statuses.includes(FILTER_CHIP.INITIAL_DIAGNOSTICS);
  const otherStatuses = statuses.filter(
    (entry) => entry !== FILTER_CHIP.INITIAL_DIAGNOSTICS
  );
  const hasOtherStatusFilter = otherStatuses.length > 0;
  const hasStudentFilter = hasStatusFilter || quickFilter !== QUICK_FILTER.ALL;

  return sessions
    // Tutor checkboxes are real tutors only; diagnostics stay visible (Status chip filters them).
    .filter(
      (session) =>
        !hasTutorFilter ||
        session.isDiagnostics ||
        tutorKeys.includes(session.tutorKey)
    )
    .map((session) => {
      const matchesDiagnosticsChip = wantsDiagnostics && session.isDiagnostics;

      // Diagnostics chip alone: keep the whole slot (including empty "slot created").
      if (matchesDiagnosticsChip && !hasOtherStatusFilter) {
        if (session.students.length === 0 || quickFilter === QUICK_FILTER.ALL) {
          return session;
        }
        const students = session.students.filter((student) =>
          matchesQuickFilter(student, quickFilter)
        );
        return students.length === session.students.length
          ? session
          : withStudents(session, students);
      }

      if (session.students.length === 0) {
        return session;
      }

      const students = session.students.filter((student) => {
        const statusOk =
          !hasStatusFilter ||
          matchesDiagnosticsChip ||
          (hasOtherStatusFilter && matchesStatusFilter(student, otherStatuses));
        return statusOk && matchesQuickFilter(student, quickFilter);
      });
      return students.length === session.students.length
        ? session
        : withStudents(session, students);
    })
    .filter((session) => {
      if (session.students.length > 0) {
        return true;
      }
      // Empty assessment slots when Initial Diagnostics is selected.
      if (session.isDiagnostics && wantsDiagnostics) {
        return true;
      }
      // Keep empty tutoring sessions unless a student-level filter is narrowing the list.
      return !hasStudentFilter;
    });
}

/** How many sessions each quick filter would show, for the panel's badges. */
export function countByQuickFilter(sessions) {
  return Object.values(QUICK_FILTER).reduce((counts, quickFilter) => {
    counts[quickFilter] = sessions.filter((session) =>
      session.students.some((student) => matchesQuickFilter(student, quickFilter))
    ).length;
    return counts;
  }, {});
}

/**
 * Group sessions into one lane per tutor for the day view.
 *
 * Lanes are ordered by tutor name so the grid does not reshuffle between refetches.
 */
export function buildTutorLanes(sessions) {
  const lanes = new Map();

  sessions.forEach((session) => {
    const tutorKey = session.isDiagnostics ? DIAGNOSTICS_LANE_KEY : session.tutorKey;
    if (!lanes.has(tutorKey)) {
      const laneName = session.isDiagnostics ? 'Initial Diagnostics' : session.tutorName;
      lanes.set(tutorKey, {
        tutorKey,
        tutorName: laneName,
        initials: initialsOf(laneName),
        colorIndex: session.laneColorIndex,
        isDiagnostics: Boolean(session.isDiagnostics),
        sessions: [],
      });
    }
    lanes.get(tutorKey).sessions.push(session);
  });

  return Array.from(lanes.values())
    .map((lane) => ({
      ...lane,
      sessionCount: lane.sessions.length,
      lessonCount: lane.sessions.reduce((total, session) => total + session.lessonCount, 0),
      subjects: Array.from(
        new Set(
          lane.sessions
            .flatMap((session) => session.students.map((student) => student.subjectName))
            .filter(Boolean)
        )
      ),
    }))
    .sort((a, b) => {
      // Keep diagnostics at the top of the day swimlane so empty slots are easy to find.
      if (a.isDiagnostics !== b.isDiagnostics) {
        return a.isDiagnostics ? -1 : 1;
      }
      return a.tutorName.localeCompare(b.tutorName);
    });
}

/**
 * Hour columns for the day and week grids.
 *
 * @param {Object[]} sessions Sessions currently visible.
 * @param {?{startHour:number, endHour:number}|{allHours:true}} operatingWindow
 *   Centre opening hours, or `{ allHours: true }` for a full 0?24 day.
 * @param {{startHour:number, endHour:number}} fallback Used when there is nothing to
 *   measure ? no sessions and no operating hours.
 */
export function buildHourWindow(sessions, operatingWindow, fallback) {
  if (operatingWindow && operatingWindow.allHours) {
    return { startHour: 0, endHour: 24 };
  }

  let startHour = null;
  let endHour = null;

  sessions.forEach((session) => {
    if (session.startHour !== null) {
      const start = Math.floor(session.startHour);
      startHour = startHour === null ? start : Math.min(startHour, start);
    }
    const end =
      session.endHour !== null && session.endHour !== undefined
        ? session.endHour
        : timeToDecimalHour(session.endTime);
    if (end !== null) {
      const ceiling = Math.ceil(end);
      endHour = endHour === null ? ceiling : Math.max(endHour, ceiling);
    }
  });

  if (operatingWindow) {
    startHour =
      startHour === null ? operatingWindow.startHour : Math.min(startHour, operatingWindow.startHour);
    endHour = endHour === null ? operatingWindow.endHour : Math.max(endHour, operatingWindow.endHour);
  }

  if (startHour === null) {
    startHour = fallback.startHour;
  }
  if (endHour === null) {
    endHour = fallback.endHour;
  }

  startHour = Math.max(0, Math.min(startHour, 23));

  return {
    startHour,
    // Always leave at least one column to render into.
    endHour: Math.min(24, Math.max(endHour, startHour + 1)),
  };
}

/** Aggregate counters for the stats strip. */
export function buildSessionStats(sessions) {
  const students = sessions.flatMap((session) => session.students);

  return {
    lessonCount: students.length,
    sessionCount: sessions.length,
    ongoingCount: students.filter((student) => student.status === DISPLAY_STATUS.ONGOING).length,
    attentionCount: students.filter((student) =>
      [
        DISPLAY_STATUS.MISSED,
        DISPLAY_STATUS.DEFER_REQUESTED,
        DISPLAY_STATUS.SLOT_ASSIGN,
        DISPLAY_STATUS.PAUSED,
      ].includes(student.status)
    ).length,
    flexiCount: students.filter((student) => student.isFlexi).length,
  };
}

/** Parse an operating-hours payload (`getCurrDateOPH`) into a grid hour window. */
export function parseOperatingWindow(operatingHours, fallback) {
  const start = timeToDecimalHour(operatingHours?.sttime);
  const end = timeToDecimalHour(operatingHours?.endtime);

  return {
    startHour: start === null ? fallback.startHour : Math.floor(start),
    endHour: end === null ? fallback.endHour : Math.ceil(end),
  };
}

/** Inclusive list of `YYYY-MM-DD` dates covering a week, starting at `firstDay`. */
export function buildWeekDates(anchorDate, firstDayOfWeek) {
  const anchor = Moment(anchorDate, 'YYYY-MM-DD');
  const start = anchor.clone().subtract((anchor.day() - firstDayOfWeek + 7) % 7, 'days');

  return Array.from({ length: 7 }, (_, offset) =>
    start.clone().add(offset, 'days').format('YYYY-MM-DD')
  );
}

/**
 * First clock hour free after a session ends.
 * Ends at 4:00 ? next mark is 5:00; ends at 4:30 ? next mark is 5:00.
 */
export function nextTimelineHourAfter(endHour) {
  if (endHour === null || endHour === undefined || Number.isNaN(endHour)) {
    return null;
  }
  const whole = Math.floor(endHour);
  return endHour === whole ? whole + 1 : Math.ceil(endHour);
}

/**
 * Build a compressed, per-tutor day timeline.
 *
 * Sessions are the primary columns. Empty create-slots are only emitted for gaps
 * *between* sessions (and at the edges of the hour window, so tutors can scroll to
 * create earlier/later). Tutors with no sessions get a single empty placeholder.
 *
 * Session segment `spanHours` is kept for labeling / end markers; column width
 * stays one card wide (no horizontal stretch by duration).
 *
 * @param {Object[]} sessions
 * @param {{startHour:number, endHour:number}} hourWindow
 * @returns {{ type: 'session'|'empty', key: string, hour: number, label: string, sessions: Object[], spanHours?: number }[]}
 */
export function buildTutorTimelineSegments(sessions, hourWindow) {
  const windowStart = hourWindow.startHour;
  const windowEnd = hourWindow.endHour;
  const valid = (sessions || []).filter((session) => {
    if (session.startHour === null || session.startHour === undefined) {
      return false;
    }
    const endHour =
      session.endHour != null && session.endHour > session.startHour
        ? session.endHour
        : session.startHour + 1;
    return endHour > windowStart && session.startHour < windowEnd;
  });
  const sorted = [...valid].sort((a, b) => {
    const startDiff = a.startHour - b.startHour;
    if (startDiff !== 0) {
      return startDiff;
    }
    return (a.endHour ?? 0) - (b.endHour ?? 0);
  });

  // Tutor with no sessions: one create slot at window start (not a full empty day).
  if (sorted.length === 0) {
    return [
      {
        type: 'empty',
        key: `empty-${windowStart}`,
        hour: windowStart,
        label: `${String(windowStart).padStart(2, '0')}:00`,
        sessions: [],
        spanHours: 1,
      },
    ];
  }

  const segments = [];
  const firstStart = Math.max(Math.floor(sorted[0].startHour), windowStart);

  // Leading empty hours before the first session (reachable by scrolling left).
  for (let hour = windowStart; hour < firstStart; hour += 1) {
    segments.push({
      type: 'empty',
      key: `empty-${hour}`,
      hour,
      label: `${String(hour).padStart(2, '0')}:00`,
      sessions: [],
      spanHours: 1,
    });
  }

  let cursor = firstStart;
  let index = 0;

  while (index < sorted.length) {
    const session = sorted[index];
    const startHour = Math.floor(session.startHour);
    const endHour =
      session.endHour != null && session.endHour > session.startHour
        ? session.endHour
        : session.startHour + 1;

    // Empty hours in the gap before this session.
    while (cursor < startHour && cursor < windowEnd) {
      segments.push({
        type: 'empty',
        key: `empty-${cursor}`,
        hour: cursor,
        label: `${String(cursor).padStart(2, '0')}:00`,
        sessions: [],
        spanHours: 1,
      });
      cursor += 1;
    }

    const group = [session];
    let groupEnd = endHour;
    index += 1;
    while (index < sorted.length && sorted[index].startHour < groupEnd - 0.001) {
      group.push(sorted[index]);
      const nextEnd =
        sorted[index].endHour != null && sorted[index].endHour > sorted[index].startHour
          ? sorted[index].endHour
          : sorted[index].startHour + 1;
      groupEnd = Math.max(groupEnd, nextEnd);
      index += 1;
    }

    const labelHour = Math.max(startHour, windowStart);
    const spanHours = Math.max(1, Math.ceil(groupEnd) - labelHour);
    segments.push({
      type: 'session',
      key: `session-${group.map((entry) => entry.key).join('-')}`,
      hour: labelHour,
      label:
        group[0].timeRangeLabel ||
        group[0].startTimeLabel ||
        `${String(labelHour).padStart(2, '0')}:00`,
      sessions: group,
      spanHours,
    });

    cursor = Math.max(cursor, nextTimelineHourAfter(groupEnd));
  }

  // Trailing empty hours after the last session (reachable by scrolling right).
  while (cursor < windowEnd) {
    segments.push({
      type: 'empty',
      key: `empty-${cursor}`,
      hour: cursor,
      label: `${String(cursor).padStart(2, '0')}:00`,
      sessions: [],
      spanHours: 1,
    });
    cursor += 1;
  }

  return segments;
}

/**
 * Whether a clock hour on `dateKey` is already in the past (centre timezone when set).
 * Used to disable "Create session" on empty tracks that have already elapsed.
 */
export function isHourInPast(dateKey, hour, now = Moment()) {
  if (!dateKey && dateKey !== 0) {
    return false;
  }

  const timezone = sessionStorage.getItem('timezone_identifier');
  const current = timezone ? Moment().tz(timezone) : Moment(now);
  const slotStart = timezone
    ? Moment.tz(
      `${dateKey} ${String(hour).padStart(2, '0')}:00:00`,
      'YYYY-MM-DD HH:mm:ss',
      timezone
    )
    : Moment(`${dateKey} ${String(hour).padStart(2, '0')}:00:00`, 'YYYY-MM-DD HH:mm:ss');

  if (!slotStart.isValid()) {
    return false;
  }

  // The hour is past once the next hour has begun (16:00?17:00 stays creatable until 17:00).
  return !current.isBefore(slotStart.clone().add(1, 'hour'));
}

function sessionEndHour(session) {
  if (session.endHour != null && session.endHour > session.startHour) {
    return session.endHour;
  }
  return session.startHour + 1;
}

function clampTimelineHour(hour, windowStart, windowEnd) {
  if (hour < windowStart) {
    return windowStart;
  }
  if (hour >= windowEnd) {
    return windowEnd - 1;
  }
  return hour;
}

/**
 * Clock hour the day swimlane should scroll to by default.
 *
 * Other dates ? window start.
 * Today ? prefer useful session content over empty "now" hours:
 *   1. ongoing session covering now
 *   2. next upcoming session
 *   3. last past session (when the day is finished)
 *   4. current clock hour (no sessions in the window)
 *
 * @param {string} dateKey
 * @param {{startHour:number, endHour:number}} hourWindow
 * @param {Object[]} [sessions] Flat list of day sessions (all tutors).
 * @param {Object} [now]
 */
export function resolveTimelineFocusHour(dateKey, hourWindow, sessions = [], now = Moment()) {
  const windowStart = hourWindow.startHour;
  const windowEnd = hourWindow.endHour;
  if (windowEnd <= windowStart) {
    return windowStart;
  }

  const timezone = sessionStorage.getItem('timezone_identifier');
  const current = timezone ? Moment().tz(timezone) : Moment(now);
  const selected = Moment(dateKey, 'YYYY-MM-DD');

  if (!selected.isValid() || !selected.isSame(current, 'day')) {
    return windowStart;
  }

  const nowHour =
    current.hour() + current.minute() / 60 + current.second() / 3600;

  const inWindow = (sessions || []).filter((session) => {
    if (session.startHour === null || session.startHour === undefined) {
      return false;
    }
    const endHour = sessionEndHour(session);
    return endHour > windowStart && session.startHour < windowEnd;
  });

  const ongoing = inWindow.filter((session) => {
    const endHour = sessionEndHour(session);
    return session.startHour <= nowHour && nowHour < endHour;
  });
  if (ongoing.length > 0) {
    const startHour = Math.min(...ongoing.map((session) => Math.floor(session.startHour)));
    return clampTimelineHour(startHour, windowStart, windowEnd);
  }

  const upcoming = inWindow
    .filter((session) => session.startHour > nowHour)
    .sort((a, b) => a.startHour - b.startHour);
  if (upcoming.length > 0) {
    return clampTimelineHour(Math.floor(upcoming[0].startHour), windowStart, windowEnd);
  }

  const past = inWindow
    .filter((session) => sessionEndHour(session) <= nowHour)
    .sort((a, b) => {
      const endDiff = sessionEndHour(b) - sessionEndHour(a);
      if (endDiff !== 0) {
        return endDiff;
      }
      return b.startHour - a.startHour;
    });
  if (past.length > 0) {
    return clampTimelineHour(Math.floor(past[0].startHour), windowStart, windowEnd);
  }

  const hour = current.hour();
  return clampTimelineHour(hour, windowStart, windowEnd);
}

